import React, { Suspense, useState, useEffect } from 'react';
import { Canvas, extend, useFrame, useThree, useLoader } from '@react-three/fiber';
import { OrbitControls, Center, Environment, useGLTF } from '@react-three/drei';
import './App.css';
// FontLoader and TextGeometry kept for potential future use
// import { FontLoader } from 'three/examples/jsm/loaders/FontLoader';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils';
import * as THREE from 'three';
import { EffectComposer, Bloom } from '@react-three/postprocessing';

// Register TextGeometry as a React component
extend({ TextGeometry })

function Stars() {
  const starCount = 2000;
  const positions = React.useMemo(() => {
    const positions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 2000;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 2000;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 2000;
    }
    return positions;
  }, []);

  // Create a circle texture programmatically
  const texture = React.useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const context = canvas.getContext('2d');
    
    // Create a radial gradient
    const gradient = context.createRadialGradient(
      16, 16, 0,    // Inner circle center (x, y) and radius
      16, 16, 16    // Outer circle center (x, y) and radius
    );
    
    // Add color stops
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    
    // Draw the gradient
    context.fillStyle = gradient;
    context.fillRect(0, 0, 32, 32);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }, []);

  return (
    <points>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={positions.length / 3}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={4}
        color="#864a92"
        sizeAttenuation
        transparent
        opacity={1}
        map={texture}
        alphaMap={texture}
        alphaTest={0.01}
        depthWrite={false}
      />
    </points>
  );
}

// First, let's create a completely separate shooting star texture
const createShootingStarTexture = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 16;
  const context = canvas.getContext('2d');
  
  // Create a horizontal gradient for the streak effect
  const gradient = context.createLinearGradient(0, 8, 64, 8);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(0.1, 'rgba(134, 74, 146, 0.8)');
  gradient.addColorStop(0.5, 'rgba(134, 74, 146, 0.3)');
  gradient.addColorStop(1, 'rgba(134, 74, 146, 0)');
  
  context.fillStyle = gradient;
  context.fillRect(0, 0, 64, 16);
  
  return new THREE.CanvasTexture(canvas);
};

// Completely separate ShootingStars component
function ShootingStars() {
  const [shootingStars, setShootingStars] = useState([]);
  const shootingStarTexture = React.useMemo(() => createShootingStarTexture(), []);

  // Create new stars
  useEffect(() => {
    const createStar = () => {
      const angle = Math.random() * Math.PI * 2;
      const radius = 300; // Distance from center where stars spawn
      
      // Start from edge of screen
      const startX = Math.cos(angle) * radius;
      const startY = Math.sin(angle) * radius;
      const startZ = (Math.random() - 0.5) * 200;

      // Calculate direction towards center with offset
      const targetX = (Math.random() - 0.5) * 100; // Slight random offset from center
      const targetY = (Math.random() - 0.5) * 100;
      const targetZ = (Math.random() - 0.5) * 100;

      // Calculate direction vector
      const dirX = (targetX - startX);
      const dirY = (targetY - startY);
      const dirZ = (targetZ - startZ);

      // Normalize direction vector
      const length = Math.sqrt(dirX * dirX + dirY * dirY + dirZ * dirZ);
      
      return {
        id: Math.random(),
        position: [startX, startY, startZ],
        direction: [dirX/length, dirY/length, dirZ/length],
        speed: 100 + Math.random() * 100,
        createdAt: Date.now(),
        lifetime: 4000 + Math.random() * 2000
      };
    };

    const interval = setInterval(() => {
      const numNewStars = 1 + Math.floor(Math.random() * 2); // 1-2 stars at a time
      setShootingStars(prev => [
        ...prev,
        ...Array(numNewStars).fill().map(() => createStar())
      ]);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Update star positions
  useFrame(({ clock }) => {
    setShootingStars(prev => 
      prev.filter(star => {
        const age = Date.now() - star.createdAt;
        return age < star.lifetime;
      }).map(star => {
        const age = Date.now() - star.createdAt;
        
        return {
          ...star,
          position: [
            star.position[0] + star.direction[0] * star.speed * 0.1,
            star.position[1] + star.direction[1] * star.speed * 0.1,
            star.position[2] + star.direction[2] * star.speed * 0.1
          ]
        };
      })
    );
  });

  return (
    <group>
      {shootingStars.map(star => {
        const age = Date.now() - star.createdAt;
        const progress = age / star.lifetime;
        const opacity = Math.min(1, Math.min(progress * 3, (1 - progress) * 3));

        return (
          <mesh
            key={star.id}
            position={star.position}
            rotation={[0, 0, Math.atan2(star.direction[1], star.direction[0])]}
          >
            <planeGeometry args={[20, 2]} />
            <meshBasicMaterial
              map={shootingStarTexture}
              transparent
              opacity={opacity}
              depthWrite={false}
              blending={THREE.AdditiveBlending}
              color="#864a92"
            />
          </mesh>
        );
      })}
    </group>
  );
}

// Chrome-purple look shared by the centre logo and the flask button, defined
// once so the two cannot drift apart. Callers add their own transparent and
// opacity values to drive their fade-in.
const CHROME_MATERIAL_PROPS = {
  color: "#9387BB",
  metalness: 0.9,
  roughness: 0.15,
  clearcoat: 1,
  clearcoatRoughness: 0.1,
  reflectivity: 1,
  envMapIntensity: 2,
  depthWrite: true
};

// Kept for easy reversion to the puffylogo.glb logo.
// eslint-disable-next-line no-unused-vars
function Title() {
  const groupRef = React.useRef();
  const [animationStartTime, setAnimationStartTime] = useState(null);
  const [modelReady, setModelReady] = useState(false);
  
  // Load the GLB model
  const { scene } = useGLTF('/models/puffylogo.glb');
  
  // Clone the scene and apply metallic material to all meshes
  const clonedScene = React.useMemo(() => {
    const cloned = scene.clone();
    
    // Create the metallic material matching the original text style
    const metallicMat = new THREE.MeshPhysicalMaterial({
      ...CHROME_MATERIAL_PROPS,
      transparent: true,
      opacity: 0
    });
    
    // Apply material to all meshes in the model
    cloned.traverse((child) => {
      if (child.isMesh) {
        // puffylogo.glb ships with positions only, and GLTFLoader does not
        // derive normals, so the metallic material would have nothing to
        // reflect the environment with.
        if (!child.geometry.attributes.normal) {
          child.geometry.computeVertexNormals();
        }
        child.material = metallicMat.clone();
      }
    });
    
    return cloned;
  }, [scene]);

  // Signal model is ready after scene is cloned
  useEffect(() => {
    if (clonedScene) {
      setModelReady(true);
    }
  }, [clonedScene]);

  // puffylogo.glb is authored at ~108 world units wide, where logo.glb was
  // ~0.93, so this keeps the on-screen width the same as before.
  const BASE_SCALE = 0.645;

  useFrame(({ clock }) => {
    if (groupRef.current && modelReady) {
      const currentTime = clock.getElapsedTime();
      
      // Start animation 0.5 seconds after model is ready
      if (!animationStartTime) {
        setAnimationStartTime(currentTime + 0.5);
        // Set initial position immediately
        groupRef.current.position.set(0, 0, -200);
        groupRef.current.scale.setScalar(0.3 * BASE_SCALE);
        
        // Set initial opacity on all meshes
        groupRef.current.traverse((child) => {
          if (child.isMesh && child.material) {
            child.material.opacity = 0;
            child.material.transparent = true;
          }
        });
        return;
      }
      
      const elapsedTime = currentTime - animationStartTime;
      const animationDuration = 3; // 3 seconds for the zoom-in effect
      
      if (elapsedTime > 0 && elapsedTime < animationDuration) {
        // Smooth easing function (ease-out cubic)
        const progress = Math.min(elapsedTime / animationDuration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        
        // Start from far back (z: -200) and move to final position (z: 0)
        const startZ = -200;
        const endZ = 0;
        const currentZ = startZ + (endZ - startZ) * easedProgress;
        
        // Scale effect - start smaller and grow to normal size (multiply by BASE_SCALE)
        const startScale = 0.3 * BASE_SCALE;
        const endScale = 1 * BASE_SCALE;
        const currentScale = startScale + (endScale - startScale) * easedProgress;
        
        groupRef.current.position.z = currentZ;
        groupRef.current.scale.setScalar(currentScale);
        
        // Update opacity on all meshes
        groupRef.current.traverse((child) => {
          if (child.isMesh && child.material) {
            child.material.opacity = easedProgress;
            child.material.transparent = true;
          }
        });
      } else if (elapsedTime >= animationDuration) {
        // Ensure final position and scale are exact
        groupRef.current.position.z = 0;
        groupRef.current.scale.setScalar(BASE_SCALE);
        
        // Set final opacity on all meshes
        groupRef.current.traverse((child) => {
          if (child.isMesh && child.material) {
            child.material.opacity = 1;
            child.material.transparent = false;
          }
        });
      }
    }
  });

  if (!modelReady) return null;

  return (
    <Center>
      {/* 3D Logo model with animation */}
      <group ref={groupRef}>
        <primitive object={clonedScene} />
      </group>
    </Center>
  );
}

// Extrusion settings expressed in the SVG's own coordinate space, which is a
// 1254 unit square. Bevel values have to be sized against that rather than the
// final on-screen size, or the rounding comes out too small to see.
//
// For a balloon look nearly all the thickness belongs to the bevel: `depth` is
// the straight-sided wall in the middle, so keeping it small leaves the profile
// almost entirely rounded.
//
// bevelSize is the constrained one. It contracts the faces inward, which shrinks
// every letter counter by that same amount, so a counter whose radius is under
// bevelSize closes over into a solid dimple. The counters here have radii of
// 9.1 to 19.7, hence HOLE_GROWTH below. bevelSize also has to stay under half
// the width of the narrowest stroke or the contracted outline crosses itself.
//
// bevelThickness is the free one: it only controls how far the surface puffs
// toward the camera and has no effect on the counters, so raise it for more
// balloon without risking anything.
const LOGO_EXTRUDE_SETTINGS = {
  depth: 4,
  bevelEnabled: true,
  bevelThickness: 22,
  bevelSize: 12,
  bevelOffset: 0,
  bevelSegments: 12
};

// The bevel shrinks each counter by bevelSize, so the counters are widened by
// the same amount beforehand and land back at roughly their designed size.
// Widening spends material from the stroke around the counter, and the tightest
// wall here is 25.6 units, so this cannot go much higher before those strokes
// get too thin to bevel cleanly.
const HOLE_GROWTH = 12;

// The tittle above the i is rebuilt rather than extruded from the SVG, because
// its spikes taper to sharp points and the main bevel rounds off everything
// within bevelSize of a tip, which left them as blobs. A smaller bevelSize keeps
// the points crisp while still reading as the same inflated material.
const STAR_EXTRUDE_SETTINGS = {
  depth: 4,
  bevelEnabled: true,
  bevelThickness: 16,
  bevelSize: 6,
  bevelOffset: 0,
  bevelSegments: 12
};

// Multipliers on the original spike lengths, for lengthening individual points.
// Each spike is scaled along its own direction, so the star keeps the slant it
// had in the SVG rather than standing straight up.
const STAR_SPIKE_SCALE = { up: 1, right: 1, down: 1, left: 1 };

// How far along each spike its curve control sits, as a fraction of the spike
// length. Smaller lets the outline dive closer to the centre, giving thinner
// arms. 0.3 puts the waist at 21.0 units, matching the 21.5 of the original
// tittle in the SVG.
const STAR_PINCH = 0.3;

// On-screen width in world units, matching what the .glb logos were scaled to.
const LOGO_WIDTH = 70;

// ExtrudeGeometry hands back non-indexed geometry and then calls
// computeVertexNormals on it, which gives every triangle one flat normal. The
// bevel is already a true quarter-ellipse, but shaded that way it reads as a
// stack of flat bands with a hard crease into the front face. Welding the
// duplicated vertices first lets the recomputed normals average across
// neighbouring bevel segments, which is what actually makes the letters look
// inflated. uv and normal are dropped beforehand because mergeVertices keys on
// every attribute, so leaving them in place would stop anything from welding;
// nothing needs uv here since the chrome material is untextured and takes its
// reflections from the environment map.
// Push every point of each counter outward from its own centre, which widens the
// opening without changing its proportions.
function widenCounters(shape) {
  if (shape.holes.length === 0) return shape;

  const widened = new THREE.Shape(shape.extractPoints(12).shape);
  widened.holes = shape.holes.map((hole) => {
    const ring = hole.getPoints(12);
    const centre = ring
      .reduce((acc, p) => acc.add(p), new THREE.Vector2())
      .divideScalar(ring.length);

    return new THREE.Path(
      ring.map((p) => {
        const dir = p.clone().sub(centre);
        if (dir.lengthSq() === 0) return p.clone();
        return p.clone().add(dir.normalize().multiplyScalar(HOLE_GROWTH));
      })
    );
  });

  return widened;
}

// Rebuilds the tittle as a four pointed sparkle, taking its centre and the
// direction of each spike from the original so the slant and position are kept.
// Scaling each tip along its own direction from the centre stretches the spike
// and its lean together, which is why the elongated star still leans with the
// letters instead of standing straight up.
function buildTittleStar(shape) {
  const pts = shape.extractPoints(12).shape;
  const pick = (test) => pts.reduce((best, p) => (test(p, best) ? p : best), pts[0]);

  const top = pick((p, b) => p.y < b.y);
  const bottom = pick((p, b) => p.y > b.y);
  const left = pick((p, b) => p.x < b.x);
  const right = pick((p, b) => p.x > b.x);

  // Where the two tip axes cross, which is what the spikes radiate from.
  const cx = (left.x + right.x) / 2;
  const cy = (top.y + bottom.y) / 2;

  const stretch = (tip, scale) => ({
    x: cx + (tip.x - cx) * scale,
    y: cy + (tip.y - cy) * scale
  });

  const tips = [
    stretch(top, STAR_SPIKE_SCALE.up),
    stretch(right, STAR_SPIKE_SCALE.right),
    stretch(bottom, STAR_SPIKE_SCALE.down),
    stretch(left, STAR_SPIKE_SCALE.left)
  ];

  const star = new THREE.Shape();
  star.moveTo(tips[0].x, tips[0].y);

  tips.forEach((tip, i) => {
    const next = tips[(i + 1) % tips.length];
    // Each edge leaves one spike along its own axis and arrives at the next along
    // that spike's axis, so it dips toward the centre in between. A single
    // quadratic cannot reach far enough in to make the arms this slender.
    star.bezierCurveTo(
      cx + (tip.x - cx) * STAR_PINCH,
      cy + (tip.y - cy) * STAR_PINCH,
      cx + (next.x - cx) * STAR_PINCH,
      cy + (next.y - cy) * STAR_PINCH,
      next.x,
      next.y
    );
  });

  return star;
}

function buildPuffyGeometry(shape, settings) {
  const geometry = new THREE.ExtrudeGeometry(shape, settings);
  geometry.deleteAttribute('uv');
  geometry.deleteAttribute('normal');

  const welded = mergeVertices(geometry);
  welded.computeVertexNormals();
  return welded;
}

function SvgLogoTitle() {
  const groupRef = React.useRef();
  const [animationStartTime, setAnimationStartTime] = useState(null);
  const svg = useLoader(SVGLoader, '/models/qrime-capital-logo.svg');

  const { model, fitScale, materials } = React.useMemo(() => {
    const group = new THREE.Group();
    const mats = [];

    // createShapes honours the file's fill-rule="evenodd", so the counters in the
    // letters come through as holes rather than filling in solid.
    const shapes = svg.paths.flatMap((path) => SVGLoader.createShapes(path));

    // The tittle above the i reaches higher than anything else in the design,
    // which is how it gets picked out for its own treatment.
    let starIndex = -1;
    let highest = Infinity;
    shapes.forEach((shape, i) => {
      const { min } = new THREE.Box2().setFromPoints(shape.extractPoints(12).shape);
      if (min.y < highest) {
        highest = min.y;
        starIndex = i;
      }
    });

    shapes.forEach((shape, i) => {
      const mat = new THREE.MeshPhysicalMaterial({
        ...CHROME_MATERIAL_PROPS,
        transparent: true,
        opacity: 0
      });
      mats.push(mat);

      const geometry =
        i === starIndex
          ? buildPuffyGeometry(buildTittleStar(shape), STAR_EXTRUDE_SETTINGS)
          : buildPuffyGeometry(widenCounters(shape), LOGO_EXTRUDE_SETTINGS);

      group.add(new THREE.Mesh(geometry, mat));
    });

    group.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(group);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    group.children.forEach((child) => child.position.sub(center));

    // SVG's Y axis points down while three.js' points up. A half turn about X
    // corrects it, and unlike a negative scale it leaves the triangle winding
    // order intact so the surfaces stay lit correctly.
    group.rotation.x = Math.PI;

    return { model: group, fitScale: LOGO_WIDTH / size.x, materials: mats };
  }, [svg]);

  useFrame(({ clock }) => {
    if (!groupRef.current) return;
    const currentTime = clock.getElapsedTime();

    if (!animationStartTime) {
      setAnimationStartTime(currentTime + 0.5);
      groupRef.current.position.set(0, 0, -200);
      groupRef.current.scale.setScalar(0.3 * fitScale);
      materials.forEach((mat) => {
        mat.transparent = true;
        mat.opacity = 0;
      });
      return;
    }

    const elapsedTime = currentTime - animationStartTime;
    const animationDuration = 3;
    if (elapsedTime <= 0) return;

    if (elapsedTime < animationDuration) {
      const progress = Math.min(elapsedTime / animationDuration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      groupRef.current.position.z = -200 + 200 * easedProgress;
      groupRef.current.scale.setScalar((0.3 + 0.7 * easedProgress) * fitScale);

      materials.forEach((mat) => {
        mat.transparent = true;
        mat.opacity = easedProgress;
      });
      return;
    }

    groupRef.current.position.z = 0;
    groupRef.current.scale.setScalar(fitScale);
    materials.forEach((mat) => {
      mat.opacity = 1;
      mat.transparent = false;
    });
  });

  return (
    <group ref={groupRef}>
      <primitive object={model} />
    </group>
  );
}

// Preload the GLB models
useGLTF.preload('/models/puffylogo.glb');
useGLTF.preload('/models/fire-template.glb');
useGLTF.preload('/models/wizard-flask.glb');
useGLTF.preload('/models/qrime-character.glb');

// Shared material properties for consistency (used in Title component inline)
// const metallicMaterial = {
//   color: "#9387BB",
//   metalness: 0.9,
//   roughness: 0.15,
//   clearcoat: 1,
//   clearcoatRoughness: 0.1,
//   reflectivity: 1,
//   envMapIntensity: 2,
//   depthWrite: true
// };

// Non-reflective material for buttons (emissive for fire glow)
const buttonMaterial = {
  color: "#9387BB",
  emissive: "#442200",
  metalness: 0.7,
  roughness: 0.3,
  clearcoat: 0,
  clearcoatRoughness: 0,
  reflectivity: 0,
  envMapIntensity: 0.5,
  depthWrite: true
};

// Procedural Voronoi flame – shaders from note-space.tauhoo.dev/experiments/3d-fire-effect-in-threejs
// Uses fire-template.glb from the demo repo for the flame mesh shape.
const FLAME_SCALE = 4;
const FLAME_ANIMATION_DURATION = 2;
const FLAME_FADE_IN_DURATION = 5;

const flameVertexShader = /* glsl */ `
precision mediump float;
#define PI 3.14
uniform float uAnimationProgress;
out vec3 vPosition;

float random(in vec2 _st) {
  return fract(sin(dot(_st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}
float wrap(float value) {
  if (value >= 1.0) return mod(value, 1.0);
  if (value < 0.0) return 1.0 - mod(value * -1.0, 1.0);
  return value;
}
vec2 wrap(vec2 value) {
  return vec2(wrap(value.x), wrap(value.y));
}
float voronoi(vec2 position, vec2 cellNumber) {
  vec2 cellSize = 1.0 / cellNumber;
  vec2 cellPosition = floor(position / cellSize) * cellSize + cellSize / 2.0;
  float maxDistance = distance(vec2(0.0), cellSize);
  float minDistance = maxDistance;
  for (float offsetX = -1.0; offsetX < 2.0; offsetX++) {
    for (float offsetY = -1.0; offsetY < 2.0; offsetY++) {
      vec2 currentCellPosition = cellPosition + vec2(offsetX, offsetY) * cellSize;
      vec2 wrapCellPosition = wrap(currentCellPosition);
      vec2 offsettedCurrentCellPosition = currentCellPosition + (random(wrapCellPosition) * 2.0 - 1.0) * cellSize / 2.0;
      float currentDistance = distance(position, offsettedCurrentCellPosition);
      if (minDistance > currentDistance) minDistance = currentDistance;
    }
  }
  return minDistance / maxDistance;
}

void main() {
  vec2 coord = vec2(atan(position.z, position.x) / (2.0 * PI), position.y * 0.5 + 0.5);
  vec2 animateOffset = vec2(0.0, -uAnimationProgress);
  float noise = voronoi(mod(coord + animateOffset, 1.0), vec2(5.0));
  float smallNoise = voronoi(mod(coord + 0.2 + animateOffset, 1.0), vec2(8.0));
  float bigNoise = voronoi(mod(coord + 0.4 + animateOffset, 1.0), vec2(3.0));
  float distortionScale = (noise + smallNoise + bigNoise) / 3.0;
  float dist = length(position.xz);
  vec2 dir = dist > 0.001 ? position.xz / dist : vec2(1.0, 0.0);
  vec2 newHorizontalPosition = dist * (1.0 - distortionScale * 0.6) * dir;
  vec3 newPosition = vec3(newHorizontalPosition.x, position.y, newHorizontalPosition.y);
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(newPosition, 1.0);
  vPosition = position;
}
`;

const flameFragmentShader = /* glsl */ `
precision mediump float;
#define PI 3.14
uniform float uAnimationProgress;
uniform vec3 uColor;
uniform float uOpacity;
in vec3 vPosition;
out vec4 fragColor;

float random(in vec2 _st) {
  return fract(sin(dot(_st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}
float wrap(float value) {
  if (value >= 1.0) return mod(value, 1.0);
  if (value < 0.0) return 1.0 - mod(value * -1.0, 1.0);
  return value;
}
vec2 wrap(vec2 value) {
  return vec2(wrap(value.x), wrap(value.y));
}
float voronoi(vec2 position, vec2 cellNumber) {
  vec2 cellSize = 1.0 / cellNumber;
  vec2 cellPosition = floor(position / cellSize) * cellSize + cellSize / 2.0;
  float maxDistance = distance(vec2(0.0), cellSize);
  float minDistance = maxDistance;
  for (float offsetX = -1.0; offsetX < 2.0; offsetX++) {
    for (float offsetY = -1.0; offsetY < 2.0; offsetY++) {
      vec2 currentCellPosition = cellPosition + vec2(offsetX, offsetY) * cellSize;
      vec2 wrapCellPosition = wrap(currentCellPosition);
      vec2 offsettedCurrentCellPosition = currentCellPosition + (random(wrapCellPosition) * 2.0 - 1.0) * cellSize / 2.0;
      float currentDistance = distance(position, offsettedCurrentCellPosition);
      if (minDistance > currentDistance) minDistance = currentDistance;
    }
  }
  return minDistance / maxDistance;
}

void main() {
  vec2 coord = vec2(atan(vPosition.z, vPosition.x) / (2.0 * PI), vPosition.y * 0.5 + 0.5);
  vec2 animateOffset = vec2(0.0, -uAnimationProgress);
  float noise = voronoi(mod(coord + animateOffset, 1.0), vec2(20.0));
  float smallNoise = voronoi(mod(coord + 0.2 + animateOffset, 1.0), vec2(30.0));
  float bigNoise = voronoi(mod(coord + 0.4 + animateOffset, 1.0), vec2(10.0));
  float verticalGradient = (-1.0 * vPosition.y * 0.5 + 0.5) * 1.3 + 0.1;
  float blend = (noise + smallNoise + bigNoise + verticalGradient) / 4.0;
  float alpha = smoothstep(0.15, 0.5, blend) * uOpacity;
  fragColor = vec4(vec3(uColor), alpha);
}
`;

// No longer rendered now that both link buttons are 3D models, kept so the
// flames can be restored by adding it back in App.
// eslint-disable-next-line no-unused-vars
function FlameCone({ basePosition, show }) {
  const { scene } = useGLTF('/models/fire-template.glb');
  const mesh1Ref = React.useRef();
  const mesh2Ref = React.useRef();

  const flameGeometry = React.useMemo(() => {
    const geo = scene.children[0]?.geometry;
    return geo ? geo.clone() : null;
  }, [scene]);

  const fadeStartRef = React.useRef(null);

  const material1 = React.useMemo(() => new THREE.ShaderMaterial({
    vertexShader: flameVertexShader,
    fragmentShader: flameFragmentShader,
    glslVersion: THREE.GLSL3,
    uniforms: {
      uAnimationProgress: { value: 0 },
      uColor: { value: new THREE.Vector3(0.525, 0.29, 0.573) },
      uOpacity: { value: 0 }
    },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide
  }), []);

  const material2 = React.useMemo(() => new THREE.ShaderMaterial({
    vertexShader: flameVertexShader,
    fragmentShader: flameFragmentShader,
    glslVersion: THREE.GLSL3,
    uniforms: {
      uAnimationProgress: { value: 0 },
      uColor: { value: new THREE.Vector3(0.576, 0.529, 0.733) },
      uOpacity: { value: 0 }
    },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide
  }), []);

  useFrame(({ clock }) => {
    if (!show) return;
    if (fadeStartRef.current === null) fadeStartRef.current = performance.now() / 1000;
    const nowSec = performance.now() / 1000;
    const fadeElapsed = nowSec - fadeStartRef.current;
    const progress = Math.min(1, fadeElapsed / FLAME_FADE_IN_DURATION);
    const opacity = progress * progress;
    material1.uniforms.uOpacity.value = opacity;
    material2.uniforms.uOpacity.value = opacity;
    const elapsed = clock.getElapsedTime();
    const t = (elapsed / FLAME_ANIMATION_DURATION) % 1;
    material1.uniforms.uAnimationProgress.value = t;
    material2.uniforms.uAnimationProgress.value = (t + 0.2) % 1;
  });

  if (!show || !flameGeometry) return null;

  const [bx, by, bz] = basePosition;
  return (
    <group position={[bx, by + FLAME_SCALE, bz]} scale={FLAME_SCALE}>
      <mesh ref={mesh1Ref} material={material1} renderOrder={0}>
        <primitive object={flameGeometry} attach="geometry" />
      </mesh>
      <mesh ref={mesh2Ref} material={material2} scale={0.9} renderOrder={1}>
        <primitive object={flameGeometry} attach="geometry" />
      </mesh>
    </group>
  );
}

// Superseded by ModelLinkButton, kept so the sphere version of the left-hand
// link can be restored by swapping the component back in App.
// eslint-disable-next-line no-unused-vars
function XButton({ show, onClick }) {
  const meshRef = React.useRef();
  const [animationStartTime, setAnimationStartTime] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isHovered, setIsHovered] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [hoverProgress, setHoverProgress] = useState(0);

  useFrame(({ clock }) => {
    if (meshRef.current && show) {
      const currentTime = clock.getElapsedTime();
      
      // Start animation when show becomes true
      if (!animationStartTime) {
        setAnimationStartTime(currentTime);
        meshRef.current.position.set(-55, 0, -200);
        meshRef.current.scale.setScalar(0.3);
        if (meshRef.current.material) {
          meshRef.current.material.opacity = 0;
          meshRef.current.material.transparent = true;
        }
        return;
      }
      
      const elapsedTime = currentTime - animationStartTime;
      const animationDuration = 1.5; // 1.5 seconds for the zoom-in effect
      
      if (elapsedTime > 0 && elapsedTime < animationDuration) {
        // Smooth easing function (ease-out cubic)
        const progress = Math.min(elapsedTime / animationDuration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        
        // Start from far back (z: -200) and move to final position (z: 0)
        const startZ = -200;
        const endZ = 0;
        const currentZ = startZ + (endZ - startZ) * easedProgress;
        
        // Scale effect - start smaller and grow to normal size
        const startScale = 0.3;
        const endScale = 1;
        const currentScale = startScale + (endScale - startScale) * easedProgress;
        
        meshRef.current.position.z = currentZ;
        meshRef.current.scale.setScalar(currentScale);
        
        // Add slight opacity fade-in effect
        if (meshRef.current.material) {
          meshRef.current.material.opacity = easedProgress;
          meshRef.current.material.transparent = true;
        }
      } else if (elapsedTime >= animationDuration) {
        // Ensure final position and scale are exact
        meshRef.current.position.z = 0;
        meshRef.current.scale.setScalar(1);
        if (meshRef.current.material) {
          meshRef.current.material.opacity = 1;
          meshRef.current.material.transparent = false;
        }
      }
      
      // Smooth hover animation
      const targetHoverProgress = isHovered ? 1 : 0;
      const hoverSpeed = 0.1; // Adjust for faster/slower hover transition
      setHoverProgress(prev => {
        const diff = targetHoverProgress - prev;
        return prev + diff * hoverSpeed;
      });
      
      // Apply hover effects + fire flicker after main animation is complete
      if (elapsedTime >= animationDuration) {
        const hoverScale = 1 + (hoverProgress * 0.2); // 20% scale increase on hover
        meshRef.current.scale.setScalar(hoverScale);
        
        if (meshRef.current.material) {
          const flicker = 0.08 * Math.sin(currentTime * 7) + 0.06 * Math.sin(currentTime * 13);
          const baseEmissive = 0.18 + flicker;
          const hoverEmissive = 0.45;
          meshRef.current.material.emissiveIntensity = baseEmissive + (hoverProgress * (hoverEmissive - baseEmissive));
        }
      }
    }
  });

  if (!show) return null;

  return (
    <mesh 
      ref={meshRef}
      position={[-55, 0, 0]}
      onClick={onClick}
      onPointerOver={() => {
        document.body.style.cursor = 'pointer';
        setIsHovered(true);
      }}
      onPointerOut={() => {
        document.body.style.cursor = 'default';
        setIsHovered(false);
      }}
    >
      <sphereGeometry 
        args={[3, 32, 32]} 
      />
      <meshPhysicalMaterial {...buttonMaterial} />
    </mesh>
  );
}

// Superseded by WizardFlaskButton, kept so the sphere-and-flame version of the
// right-hand link can be restored by swapping the component back in App.
// eslint-disable-next-line no-unused-vars
function TriangleButton({ show, onClick }) {
  const meshRef = React.useRef();
  const [animationStartTime, setAnimationStartTime] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isHovered, setIsHovered] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [hoverProgress, setHoverProgress] = useState(0);

  useFrame(({ clock }) => {
    if (meshRef.current && show) {
      const currentTime = clock.getElapsedTime();
      
      // Start animation when show becomes true
      if (!animationStartTime) {
        setAnimationStartTime(currentTime);
        meshRef.current.position.set(55, 0, -200);
        meshRef.current.scale.setScalar(0.3);
        if (meshRef.current.material) {
          meshRef.current.material.opacity = 0;
          meshRef.current.material.transparent = true;
        }
        return;
      }
      
      const elapsedTime = currentTime - animationStartTime;
      const animationDuration = 1.5; // 1.5 seconds for the zoom-in effect
      
      if (elapsedTime > 0 && elapsedTime < animationDuration) {
        // Smooth easing function (ease-out cubic)
        const progress = Math.min(elapsedTime / animationDuration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);
        
        // Start from far back (z: -200) and move to final position (z: 0)
        const startZ = -200;
        const endZ = 0;
        const currentZ = startZ + (endZ - startZ) * easedProgress;
        
        // Scale effect - start smaller and grow to normal size
        const startScale = 0.3;
        const endScale = 1;
        const currentScale = startScale + (endScale - startScale) * easedProgress;
        
        meshRef.current.position.z = currentZ;
        meshRef.current.scale.setScalar(currentScale);
        
        // Add slight opacity fade-in effect
        if (meshRef.current.material) {
          meshRef.current.material.opacity = easedProgress;
          meshRef.current.material.transparent = true;
        }
      } else if (elapsedTime >= animationDuration) {
        // Ensure final position and scale are exact
        meshRef.current.position.z = 0;
        meshRef.current.scale.setScalar(1);
        if (meshRef.current.material) {
          meshRef.current.material.opacity = 1;
          meshRef.current.material.transparent = false;
        }
      }
      
      // Smooth hover animation
      const targetHoverProgress = isHovered ? 1 : 0;
      const hoverSpeed = 0.1; // Adjust for faster/slower hover transition
      setHoverProgress(prev => {
        const diff = targetHoverProgress - prev;
        return prev + diff * hoverSpeed;
      });
      
      // Apply hover effects + fire flicker after main animation is complete
      if (elapsedTime >= animationDuration) {
        const hoverScale = 1 + (hoverProgress * 0.2); // 20% scale increase on hover
        meshRef.current.scale.setScalar(hoverScale);
        
        if (meshRef.current.material) {
          const flicker = 0.08 * Math.sin(currentTime * 7) + 0.06 * Math.sin(currentTime * 13);
          const baseEmissive = 0.18 + flicker;
          const hoverEmissive = 0.45;
          meshRef.current.material.emissiveIntensity = baseEmissive + (hoverProgress * (hoverEmissive - baseEmissive));
        }
      }
    }
  });

  if (!show) return null;

  return (
    <mesh 
      ref={meshRef}
      position={[55, 0, 0]}
      onClick={onClick}
      onPointerOver={() => {
        document.body.style.cursor = 'pointer';
        setIsHovered(true);
      }}
      onPointerOut={() => {
        document.body.style.cursor = 'default';
        setIsHovered(false);
      }}
    >
      <sphereGeometry args={[3, 32, 32]} />
      <meshPhysicalMaterial {...buttonMaterial} />
    </mesh>
  );
}

// Drop-in replacement for the sphere-and-flame link buttons. `height` is the
// model's on-screen height in world units, and it is scaled to suit whatever
// size it happens to be authored at. Set `zUp` for assets exported Z-up, which
// trimesh does by default.
function ModelLinkButton({ url, show, onClick, x, height, zUp = false }) {
  const groupRef = React.useRef();
  const { scene } = useGLTF(url);
  const [animationStartTime, setAnimationStartTime] = useState(null);
  const isHoveredRef = React.useRef(false);
  const hoverProgressRef = React.useRef(0);

  const { model, fitScale, materials } = React.useMemo(() => {
    const cloned = scene.clone(true);

    if (zUp) cloned.rotation.x = -Math.PI / 2;
    cloned.updateMatrixWorld(true);

    // Recentre on the origin so the group scales about the middle of the model
    // rather than its base.
    const box = new THREE.Box3().setFromObject(cloned);
    const size = box.getSize(new THREE.Vector3());
    cloned.position.sub(box.getCenter(new THREE.Vector3()));

    // Discard the asset's own materials in favour of the same chrome purple as
    // the centre logo. side is carried over from each original so double-sided
    // shell geometry, like the flask's glass, does not develop holes.
    const mats = [];
    cloned.traverse((child) => {
      if (child.isMesh && child.material) {
        const mat = new THREE.MeshPhysicalMaterial({
          ...CHROME_MATERIAL_PROPS,
          side: child.material.side,
          transparent: true,
          opacity: 0
        });
        child.material = mat;
        mats.push({ mat, baseOpacity: 1, baseTransparent: false });
      }
    });

    return { model: cloned, fitScale: height / size.y, materials: mats };
  }, [scene, height, zUp]);

  useFrame(({ clock }) => {
    if (!groupRef.current || !show) return;
    const currentTime = clock.getElapsedTime();

    if (!animationStartTime) {
      setAnimationStartTime(currentTime);
      groupRef.current.position.set(x, 0, -200);
      groupRef.current.scale.setScalar(0.3 * fitScale);
      materials.forEach(({ mat }) => {
        mat.transparent = true;
        mat.opacity = 0;
      });
      return;
    }

    const elapsedTime = currentTime - animationStartTime;
    const animationDuration = 1.5;

    if (elapsedTime > 0 && elapsedTime < animationDuration) {
      const progress = Math.min(elapsedTime / animationDuration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      groupRef.current.position.z = -200 + 200 * easedProgress;
      groupRef.current.scale.setScalar((0.3 + 0.7 * easedProgress) * fitScale);

      materials.forEach(({ mat, baseOpacity }) => {
        mat.transparent = true;
        mat.opacity = baseOpacity * easedProgress;
      });
      return;
    }

    groupRef.current.position.z = 0;
    materials.forEach(({ mat, baseOpacity, baseTransparent }) => {
      mat.opacity = baseOpacity;
      mat.transparent = baseTransparent;
    });

    // Same 20% growth on hover as the sphere this replaced.
    const targetHoverProgress = isHoveredRef.current ? 1 : 0;
    hoverProgressRef.current += (targetHoverProgress - hoverProgressRef.current) * 0.1;
    groupRef.current.scale.setScalar(fitScale * (1 + hoverProgressRef.current * 0.2));
  });

  if (!show) return null;

  return (
    <group
      ref={groupRef}
      position={[x, 0, 0]}
      onClick={onClick}
      onPointerOver={() => {
        document.body.style.cursor = 'pointer';
        isHoveredRef.current = true;
      }}
      onPointerOut={() => {
        document.body.style.cursor = 'default';
        isHoveredRef.current = false;
      }}
    >
      <primitive object={model} />
    </group>
  );
}

const BUTTON_1_APPEAR_MS = 3500;
const BUTTON_2_APPEAR_MS = 4500;
function getCameraZ() {
  const aspect = window.innerWidth / window.innerHeight;
  const minVisibleX = 70;
  return Math.max(100, minVisibleX / (Math.tan(75 * Math.PI / 360) * aspect));
}

function CameraSync() {
  const { camera } = useThree();

  useEffect(() => {
    const onResize = () => {
      camera.position.z = getCameraZ();
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [camera]);

  return null;
}

function App() {
  const [cameraZ] = useState(getCameraZ);
  const [showButton1, setShowButton1] = useState(false);
  const [showButton2, setShowButton2] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setShowButton1(true), BUTTON_1_APPEAR_MS);
    const t2 = setTimeout(() => setShowButton2(true), BUTTON_2_APPEAR_MS);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const handleXClick = () => {
    window.open('https://x.com/qrimeCapital', '_blank');
  };

  const handleTriangleClick = () => {
    window.open('https://x.com/TheLabProtocol', '_blank');
  };

  return (
    <div className="App">
      <Canvas
        camera={{ position: [0, 0, cameraZ], fov: 75 }}
        style={{ background: '#000000' }}
      >
        <CameraSync />
        <Suspense fallback={null}>
          <Environment preset="city" />
          
          <ambientLight intensity={0.2} />
          <pointLight position={[10, 10, 10]} intensity={0.5} />
          <Stars />
          <ShootingStars />
          <SvgLogoTitle />
          <ModelLinkButton
            url="/models/qrime-character.glb"
            x={-55}
            height={14}
            show={showButton1}
            onClick={handleXClick}
          />
          <ModelLinkButton
            url="/models/wizard-flask.glb"
            x={55}
            height={12}
            zUp
            show={showButton2}
            onClick={handleTriangleClick}
          />
          <OrbitControls
            enableZoom={false}
            enablePan={false}
            autoRotate
            autoRotateSpeed={0.5}
          />
          <Effects />
        </Suspense>
      </Canvas>
    </div>
  );
}

function Effects() {
  return (
    <EffectComposer>
      <Bloom
        intensity={1.5}
        luminanceThreshold={0.6}
        luminanceSmoothing={0.9}
      />
    </EffectComposer>
  );
}

export default App;